import { useRef, useState, useEffect } from "react";
import { Mic, Square, Play, Pause, RotateCcw, Check, Trash2, Upload } from "lucide-react";
import { Waveform } from "@/components/Waveform";

function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

const MIN = 60;
const MAX = 240;

function lengthIssue(s) {
  if (s < MIN) return `This recording is ${fmt(s)}. Recordings need to be at least 1 minute — please record a little more.`;
  if (s > MAX + 1) return `This recording is ${fmt(s)}. Recordings can be up to 4 minutes — please record a shorter version.`;
  return "";
}

function audioLength(file) {
  return new Promise((resolve) => {
    const a = new Audio();
    a.preload = "metadata";
    a.onloadedmetadata = () => resolve(Number.isFinite(a.duration) ? a.duration : 0);
    a.onerror = () => resolve(0);
    a.src = URL.createObjectURL(file);
  });
}

export function VoiceRecorder({ onSave, saving, saveLabel = "Save Recording" }) {
  const [state, setState] = useState("idle"); // idle | recording | paused | recorded
  const [seconds, setSeconds] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const blobRef = useRef(null);
  const nameRef = useRef("memory.webm");
  const audioRef = useRef(null);
  const fileRef = useRef(null);
  const busyRef = useRef(false);
  const aliveRef = useRef(true);
  const elapsedRef = useRef(0);
  const segStartRef = useRef(0);
  const controlsRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const clearTimer = () => { clearInterval(timerRef.current); timerRef.current = null; };
  const releaseMic = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  const elapsed = () => elapsedRef.current + (segStartRef.current ? performance.now() - segStartRef.current : 0);
  const freeze = () => { elapsedRef.current = elapsed(); segStartRef.current = 0; };
  const discardRecorder = () => {
    const mr = mediaRef.current;
    if (mr) { mr.onstop = null; try { if (mr.state !== "inactive") mr.stop(); } catch (e) { /* noop */ } }
    mediaRef.current = null;
  };

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  useEffect(() => { if (state === "recording") controlsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [state]);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; clearTimer(); discardRecorder(); releaseMic(); };
  }, []);

  const stop = () => {
    clearTimer();
    freeze();
    setSeconds(Math.min(MAX, Math.floor(elapsedRef.current / 1000)));
    const mr = mediaRef.current;
    if (mr && mr.state !== "inactive") mr.stop(); else releaseMic();
  };

  const tick = () => {
    clearTimer();
    segStartRef.current = performance.now();
    timerRef.current = setInterval(() => {
      const secs = Math.floor(elapsed() / 1000);
      if (secs >= MAX) { stop(); return; }
      setSeconds(secs);
    }, 250);
  };

  const start = async () => {
    if (busyRef.current || (mediaRef.current && mediaRef.current.state !== "inactive")) return;
    busyRef.current = true;
    setStarting(true);
    setError("");
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!aliveRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      clearTimer();
      releaseMic();
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        blobRef.current = new Blob(chunksRef.current, { type: "audio/webm" });
        nameRef.current = "memory.webm";
        releaseMic();
        if (!aliveRef.current) return;
        setPreviewUrl(URL.createObjectURL(blobRef.current));
        setState("recorded");
      };
      mediaRef.current = mr;
      mr.start();
      elapsedRef.current = 0;
      setSeconds(0);
      setState("recording");
      tick();
    } catch (e) {
      clearTimer();
      stream?.getTracks().forEach((t) => t.stop());
      releaseMic();
      mediaRef.current = null;
      if (aliveRef.current) {
        setState("idle");
        setError("We couldn't reach your microphone. Please allow microphone access, or upload a recording instead.");
      }
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setStarting(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError("");
    const secs = await audioLength(f);
    blobRef.current = f;
    nameRef.current = f.name || "upload.mp3";
    setSeconds(Math.round(secs));
    setPreviewUrl(URL.createObjectURL(f));
    setState("recorded");
  };

  const pause = () => {
    if (mediaRef.current?.state === "recording") {
      mediaRef.current.pause();
      clearTimer();
      freeze();
      setSeconds(Math.floor(elapsedRef.current / 1000));
      setState("paused");
    }
  };
  const resume = () => {
    if (mediaRef.current?.state === "paused") {
      mediaRef.current.resume();
      setState("recording");
      tick();
    }
  };
  const reset = () => {
    clearTimer();
    discardRecorder();
    releaseMic();
    elapsedRef.current = 0; segStartRef.current = 0;
    setState("idle"); setSeconds(0); setPlaying(false); blobRef.current = null; setError("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause(); else audioRef.current.play();
    setPlaying(!playing);
  };
  const save = () => {
    const issue = seconds ? lengthIssue(seconds) : "";
    if (issue) return setError(issue);
    onSave(blobRef.current, seconds, nameRef.current);
  };
  const remaining = MIN - seconds;

  return (
    <div className="flex flex-col items-center gap-6" data-testid="voice-recorder">
      {state === "idle" && (
        <>
          <p className="text-center text-lg text-[#B9B3C8] max-w-md">
            When you're ready, press the button and simply start talking.
          </p>
          <button data-testid="start-recording-btn" onClick={start} disabled={starting} aria-busy={starting}
            aria-label={starting ? "Starting microphone" : "Start recording"}
            className="relative h-24 w-24 rounded-full gold-btn flex items-center justify-center disabled:opacity-60 disabled:cursor-wait">
            <Mic className={`h-11 w-11 ${starting ? "animate-pulse" : ""}`} />
          </button>
          {starting && <p className="text-[#C9B6FF]" data-testid="recorder-starting" aria-live="polite">Starting your microphone…</p>}
          <p className="text-center text-[#C9C2D8]" data-testid="recording-length-guidance">
            Each recording should be between <strong className="text-[#EDE6F7]">1 and 4 minutes</strong>.
          </p>
          <button type="button" data-testid="upload-audio-btn" onClick={() => fileRef.current?.click()} disabled={starting}
            className="text-[#B57BFF] underline underline-offset-4 flex items-center gap-2 disabled:opacity-60">
            <Upload className="h-4 w-4" aria-hidden="true" /> Or upload a recording (MP3, M4A, WAV or WebM)
          </button>
          <input ref={fileRef} type="file" accept="audio/*,.mp3,.m4a,.wav,.webm,.ogg" className="hidden" onChange={onFile} data-testid="upload-audio-input" />
        </>
      )}

      {(state === "recording" || state === "paused") && (
        <>
          <div className="text-5xl font-serif text-[#EDE6F7] tabular-nums" data-testid="recording-timer" aria-live="off">
            {fmt(seconds)} <span className="text-2xl text-[#8C84A0]">/ 4:00</span>
          </div>
          <p className="text-[#C9C2D8]" data-testid="recording-length-feedback" aria-live="polite">
            {remaining > 0 ? `Keep going — at least ${fmt(remaining)} more to reach 1 minute.` :
              seconds >= MAX - 30 ? `${fmt(MAX - seconds)} left. Recording stops automatically at 4:00.` : "Lovely — you can stop whenever you're ready."}
          </p>
          <Waveform bars={40} height={44} active={state === "recording"} />
          <div ref={controlsRef} className="flex items-center gap-4 scroll-mb-6">
            {state === "recording" ? (
              <button data-testid="pause-recording-btn" onClick={pause}
                className="ghost-gold h-14 px-6 rounded-full flex items-center text-lg">
                <Pause className="h-5 w-5 mr-2" /> Pause
              </button>
            ) : (
              <button data-testid="resume-recording-btn" onClick={resume}
                className="ghost-gold h-14 px-6 rounded-full flex items-center text-lg">
                <Play className="h-5 w-5 mr-2" /> Continue
              </button>
            )}
            <button data-testid="stop-recording-btn" onClick={stop} aria-label="Stop recording"
              className="rec-ring relative h-20 w-20 rounded-full bg-[#EF4444] text-[#F6F1FD] flex items-center justify-center">
              <Square className="h-8 w-8" fill="white" />
            </button>
          </div>
        </>
      )}

      {state === "recorded" && (
        <>
          <audio ref={audioRef} src={previewUrl} onEnded={() => setPlaying(false)} className="hidden" />
          <div className="text-3xl font-serif text-[#EDE6F7] tabular-nums" data-testid="recorded-length">{fmt(seconds)}</div>
          {seconds > 0 && lengthIssue(seconds) && (
            <p className="text-center text-[#E6CCF2] max-w-md" data-testid="recording-length-warning">{lengthIssue(seconds)}</p>
          )}
          <Waveform bars={40} height={40} active={playing} />
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button data-testid="preview-play-btn" onClick={togglePlay}
              className="ghost-gold h-13 px-5 py-3 rounded-full flex items-center text-lg">
              {playing ? <Pause className="h-5 w-5 mr-2" /> : <Play className="h-5 w-5 mr-2" />}
              {playing ? "Pause" : "Listen back"}
            </button>
            <button data-testid="rerecord-btn" onClick={reset}
              className="ghost-gold h-13 px-5 py-3 rounded-full flex items-center text-lg">
              <RotateCcw className="h-5 w-5 mr-2" /> Record again
            </button>
            <button data-testid="delete-recording-btn" onClick={reset}
              className="h-13 px-5 py-3 rounded-full flex items-center text-[#B9B3C8] hover:text-[#EF4444] transition-colors">
              <Trash2 className="h-5 w-5 mr-2" /> Delete
            </button>
          </div>
          <button data-testid="save-recording-btn" disabled={saving || (seconds > 0 && !!lengthIssue(seconds))} onClick={save}
            className="gold-btn h-16 px-10 rounded-full text-xl flex items-center disabled:opacity-60">
            <Check className="h-6 w-6 mr-2" />
            {saving ? "Saving…" : saveLabel}
          </button>
        </>
      )}

      {error && <p role="alert" className="text-[#E6CCF2] text-center max-w-md" data-testid="recorder-error">{error}</p>}
    </div>
  );
}
